import React, { useContext, useMemo } from 'react';

import { Accordion } from '@mui/material';
import Button from '@mui/material/Button';
import DOMPurify from 'dompurify';
import { browserName, browserVersion, fullBrowserVersion } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DownloadContext } from './context/DownloadContext';
import { BrowserCheckResults, UnsupportedBrowserScreen } from './UnsupportedBrowserScreen';
import ImgBgGridPurple from '../../images/grid-background-class-purple.png';
import ImgHeroTeams from '../../images/hero-class-for-teams.png';
import ImgHero from '../../images/hero-download-classroom-view-avatars.png';

const Container = styled('div')`
    height: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
`;

const MainCol = styled('div')`
    display: flex;
    align-items: center;
    text-align: center;
`;

const MainCol1 = styled(MainCol)`
    display: flex;
    align-items: center;
    text-align: center;
`;

const MainCol2 = styled(MainCol)`
    background-image: url(${ImgBgGridPurple});
    background-repeat: no-repeat;
    background-size: cover;
    color: #fff;
`;

const MainColBody = styled('div')`
    width: calc(100% - 96px);
    margin: 1% auto;
`;

const HeroTitle = styled('h2')`
    font-style: normal;
    font-weight: 400;
    font-size: 30px;
    line-height: 35px;
`;

const HeroImg = styled('img')`
    width: 100%;
`;

const DownloadOptions = styled('div')`
    display: inline-block;
    width: 375px;
    max-width: 110%;
    padding: 24px;
    background-color: #ffffff;
    box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px rgb(0 0 0 / 14%), 0px 1px 10px rgb(0 0 0 / 12%);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0 16px;
    h2 {
        line-height: 35.16px;
        margin: 20px 0 16px;
        font-weight: 500;
        font-size: 30px;
    }
`;

const LaunchWebApp = styled('div')`
    display: inline-block;
    width: 375px;
    max-width: 110%;
    padding: 24px;
    background-color: #ffffff;
    box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px rgb(0 0 0 / 14%), 0px 1px 10px rgb(0 0 0 / 12%);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0 16px;
    h2 {
        line-height: 35.16px;
        margin: 20px 0 16px;
        font-weight: 500;
        font-size: 30px;
    }
`;

const AlreadyJoinedText = styled('div')`
    margin: 24px 0;
    font-size: 18px;
`;

const LogoImage = styled('img')`
    display: block;
    max-height: 20vh;
    max-width: calc(80% - 3rem);
    margin: 1.5rem auto;
`;

const DeviceWarning = styled('div')`
    text-align: left;
    margin: 0.5rem 0 1rem;
    padding: 8px 16px;
    background-color: ${(props) => props.theme.colors.primary.light};
    h3 {
        margin-top: 0;
        font-weight: 500;
        font-size: 16px;
    }
    p {
        margin-bottom: 0;
    }
`;

const SupportedDevices = styled('div')`
    margin: 0 0 1rem;
    border: 1px solid ${(props) => props.theme.colors.primary.light};
    h3 {
        font-weight: 400;
        font-size: 16px;
    }
`;

const SupportedDevicesGrid = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    > div {
        padding: 1rem;
        text-align: center;
    }
`;

const StyledAccordion = styled(Accordion)`
    text-align: left;
    margin: 0;
`;

const browserSupport = {
    teams: {
        chrome: 100,
        edge: 100,
        safari: 17
    },
    zoom: {
        chrome: 100,
        edge: 100,
        safari: 16
    }
};

const Body: React.FC = () => {
    const { schoolInfo, userAgent, adminSettings, downloadURL, isTeamsSchool, isHipaa } = useContext(DownloadContext);
    const { t } = useTranslation('class');

    const browserCheckResult = useMemo(() => {
        const config = isTeamsSchool ? browserSupport.teams : browserSupport.zoom;
        if (browserName) {
            const browserNameLower = browserName.toLowerCase();

            if (!config.hasOwnProperty(browserNameLower)) {
                console.warn('Browser is not supported', { browserNameLower });

                return BrowserCheckResults.Unsupported;
            }

            const requiredVersion: number = config[browserNameLower];

            const browserVersionNum = parseInt(browserVersion, 10);

            if (isNaN(browserVersionNum)) {
                console.warn('Unable to determine browser version', {
                    browserName,
                    browserVersion,
                    fullBrowserVersion
                });

                return BrowserCheckResults.Supported;
            }

            if (browserVersionNum < requiredVersion) {
                console.warn('Browser not supported by current configuration', {
                    browserName,
                    browserVersion,
                    requiredVersion,
                    fullBrowserVersion
                });

                return BrowserCheckResults.OutOfDate;
            }
        }

        return BrowserCheckResults.Supported;
    }, [isTeamsSchool]);

    // There's no way that our language strings are going to result in XSS, but I'm purifying them anyway.
    const tHTML = (key: string) => ({ __html: DOMPurify.sanitize(t(key)) });
    return (
        <Container>
            <MainCol1>
                <MainColBody className="main-column-body">
                    {schoolInfo?.logo && (
                        <LogoImage src={schoolInfo?.logo} id="logo-image" alt="" role="presentation" />
                    )}

                    {browserCheckResult === BrowserCheckResults.Unsupported && (
                        <UnsupportedBrowserScreen reason={BrowserCheckResults.Unsupported} />
                    )}
                    {browserCheckResult === BrowserCheckResults.OutOfDate && (
                        <UnsupportedBrowserScreen reason={BrowserCheckResults.OutOfDate} />
                    )}
                    {browserCheckResult === BrowserCheckResults.Supported ? (
                        <>
                            <LaunchWebApp>
                                <h2 dangerouslySetInnerHTML={tHTML('signin-class-web')}></h2>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        window.location.href = '/v1_index_client.html?websdk=true';
                                    }}
                                >
                                    {t('sign-in')}
                                </Button>
                            </LaunchWebApp>
                        </>
                    ) : (
                        <> </>
                    )}
                </MainColBody>
            </MainCol1>
            <MainCol2>
                <MainColBody>
                    <HeroTitle className="hero-title">{t(isTeamsSchool ? 'hero-title-teams' : 'hero-title')}</HeroTitle>
                    <HeroImg src={isTeamsSchool ? ImgHeroTeams : ImgHero} alt="" role="presentation" />
                </MainColBody>
            </MainCol2>
        </Container>
    );
};

export default Body;
